/**
 * Dashboard Page
 * Shows real activity metrics, standards effectiveness, and team overview
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card } from 'flowbite-react';
import { useStatus } from '../contexts/StatusContext';
import {
  HiCode,
  HiUpload,
  HiTrendingUp,
  HiShieldCheck,
  HiUserGroup,
  HiChartBar,
  HiLightBulb,
  HiArrowRight,
} from 'react-icons/hi';
import { githubApi } from '../api/github';
import { enterpriseApi } from '../api/enterprise';
import { activityApi, PersonalActivity } from '../api/activity';
import { analyticsApi, ActivitySummary } from '../api/analytics';

const Dashboard: React.FC = () => {
  const { user, client, usageStats, hasFeature } = useStatus();
  const navigate = useNavigate();

  const [activity, setActivity] = useState<PersonalActivity | null>(null);
  const [summary, setSummary] = useState<ActivitySummary | null>(null);
  const [teamData, setTeamData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  // Redirect to onboarding if user has no projects connected
  useEffect(() => {
    const checkOnboarding = async () => {
      const email = user?.email || localStorage.getItem('mindmeld_email') || '';
      if (localStorage.getItem(`mindmeld_onboarding_skipped_${email}`)) return;
      if (usageStats?.projects === 0) {
        const status = await githubApi.getStatus();
        const noProjectsConnected = !status.success || !status.data || !status.data.projects_connected;
        if (noProjectsConnected) {
          if (client?.subscription_tier === 'enterprise') {
            const entStatus = await enterpriseApi.getOnboardingStatus();
            if (entStatus.success && entStatus.data && !entStatus.data.completed) {
              navigate('/enterprise/onboarding', { replace: true });
            }
            return;
          }
          navigate('/onboarding', { replace: true });
        }
      }
    };
    checkOnboarding();
  }, [usageStats, client, navigate]);

  // Fetch activity data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [activityResult, summaryResult] = await Promise.all([
          activityApi.getMyActivity(),
          analyticsApi.getActivitySummary('30d'),
        ]);

        if (activityResult.success && activityResult.data) {
          setActivity(activityResult.data);
        }
        if (summaryResult.success && summaryResult.data) {
          setSummary(summaryResult.data);
        }

        // Fetch team data for Pro+ users
        if (hasFeature('analytics')) {
          const teamResult = await activityApi.getTeamActivity();
          if (teamResult.success && teamResult.data) {
            setTeamData(teamResult.data);
          }
        }
      } catch (err) {
        console.error('[Dashboard] Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [hasFeature]);

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'enterprise': return 'text-purple-600';
      case 'professional': return 'text-blue-600';
      case 'team':
      case 'starter': return 'text-green-600';
      default: return 'text-gray-600';
    }
  };

  const tierLabel = (client?.subscription_tier || 'free').charAt(0).toUpperCase() +
    (client?.subscription_tier || 'free').slice(1);

  const sessions30d = activity?.sessions?.last_30_days ?? 0;
  const sessions7d = activity?.sessions?.last_7_days ?? 0;
  const commits30d = activity?.commits?.last_30_days ?? 0;
  const daysSinceCommit = activity?.commits?.days_since_commit;
  const conversionPct = activity?.value_correlation?.session_to_commit_conversion_pct ?? 0;
  const standardsCount = summary?.top_standards?.length ?? 0;
  const injectionsTotal = summary?.injections?.total ?? 0;
  const violationsTotal = summary?.violations?.total ?? 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="mt-1 text-sm text-gray-500">
            Welcome back, {user?.display_name || user?.email}
          </p>
        </div>
        <div className="text-right">
          <span className={`text-lg font-bold ${getTierColor(client?.subscription_tier || 'free')}`}>
            {tierLabel}
          </span>
          <p className="text-xs text-gray-500">
            {client?.subscription_status === 'active' ? 'Active' : client?.subscription_status}
            {client?.subscription_tier !== 'enterprise' && (
              <Link to="/subscriptions" className="ml-2 text-blue-600 hover:underline">Upgrade</Link>
            )}
          </p>
        </div>
      </div>

      {/* Activity Summary — 4 cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-blue-100">
              <HiCode className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Sessions</p>
              <p className="text-2xl font-bold text-gray-900">{sessions30d}</p>
              <p className="text-xs text-gray-400">{sessions7d} this week</p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-100">
              <HiUpload className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Commits</p>
              {commits30d > 0 ? (
                <>
                  <p className="text-2xl font-bold text-gray-900">{commits30d}</p>
                  <p className="text-xs text-gray-400">
                    {daysSinceCommit === 0 ? 'Last commit today' : `${daysSinceCommit}d since last`}
                  </p>
                </>
              ) : (
                <p className="text-sm text-gray-400 mt-0.5">Connect GitHub to track commits</p>
              )}
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-amber-100">
              <HiTrendingUp className="h-6 w-6 text-amber-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Conversion Rate</p>
              <p className="text-2xl font-bold text-gray-900">{conversionPct}%</p>
              <p className="text-xs text-gray-400">sessions to commits</p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-purple-100">
              <HiShieldCheck className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Standards Active</p>
              <p className="text-2xl font-bold text-gray-900">{standardsCount}</p>
              <p className="text-xs text-gray-400">{injectionsTotal} injections this month</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Standards Effectiveness */}
      <Card>
        <h3 className="text-lg font-semibold text-gray-900 mb-3">Standards Effectiveness</h3>
        {loading ? (
          <p className="text-sm text-gray-400">Loading...</p>
        ) : injectionsTotal === 0 && standardsCount === 0 ? (
          <div className="text-center py-4">
            <p className="text-sm text-gray-500">No standards activity yet.</p>
            <p className="text-xs text-gray-400 mt-1">
              Standards are injected automatically during your Claude Code sessions.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Injections */}
            <div className="bg-blue-50 rounded-lg p-4">
              <p className="text-sm font-medium text-blue-800">Injections</p>
              <p className="text-2xl font-bold text-blue-900">{injectionsTotal}</p>
              <p className="text-xs text-blue-600">standards injected this month</p>
            </div>

            {/* Top Standards */}
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm font-medium text-gray-700 mb-2">Most Used</p>
              {summary?.top_standards && summary.top_standards.length > 0 ? (
                <ul className="space-y-1">
                  {summary.top_standards.slice(0, 3).map((s, i) => (
                    <li key={i} className="text-xs text-gray-600 flex justify-between">
                      <span className="truncate mr-2">{s.standard_name}</span>
                      <span className="text-gray-400 shrink-0">{s.count}x</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-gray-400">No data yet</p>
              )}
            </div>

            {/* Violations */}
            <div className={`rounded-lg p-4 ${violationsTotal > 0 ? 'bg-red-50' : 'bg-gray-50'}`}>
              <p className={`text-sm font-medium ${violationsTotal > 0 ? 'text-red-800' : 'text-gray-700'}`}>
                Violations
              </p>
              {violationsTotal > 0 ? (
                <>
                  <p className="text-2xl font-bold text-red-900">{violationsTotal}</p>
                  <p className="text-xs text-red-600">detected this month</p>
                </>
              ) : (
                <p className="text-sm text-gray-500 mt-1">Compliance tracking not yet active</p>
              )}
            </div>
          </div>
        )}
      </Card>

      {/* Team Overview — Pro+ only */}
      {hasFeature('analytics') && teamData && (
        <Card>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold text-gray-900">Team Overview</h3>
            <span className="text-xs text-gray-400">
              {teamData.summary?.active_developers ?? 0} / {teamData.summary?.total_developers ?? 0} active
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-gray-900">
                {teamData.summary?.active_developers ?? 0}
              </p>
              <p className="text-xs text-gray-500">Active Developers</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-gray-900">
                {(teamData.summary?.avg_sessions_30d ?? 0).toFixed(0)}
              </p>
              <p className="text-xs text-gray-500">Avg Sessions / Dev</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-gray-900">
                {(teamData.summary?.avg_conversion_pct ?? 0).toFixed(0)}%
              </p>
              <p className="text-xs text-gray-500">Avg Conversion Rate</p>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Link
              to="/manager"
              className="flex items-center justify-between p-3 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
            >
              <span className="text-sm font-medium text-blue-800 flex items-center">
                <HiUserGroup className="mr-2 h-4 w-4" /> Manager View
              </span>
              <HiArrowRight className="h-4 w-4 text-blue-600" />
            </Link>
            <Link
              to="/reports"
              className="flex items-center justify-between p-3 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors"
            >
              <span className="text-sm font-medium text-purple-800 flex items-center">
                <HiChartBar className="mr-2 h-4 w-4" /> Reports
              </span>
              <HiArrowRight className="h-4 w-4 text-purple-600" />
            </Link>
            <Link
              to="/insights"
              className="flex items-center justify-between p-3 bg-green-50 rounded-lg hover:bg-green-100 transition-colors"
            >
              <span className="text-sm font-medium text-green-800 flex items-center">
                <HiLightBulb className="mr-2 h-4 w-4" /> Insights
              </span>
              <HiArrowRight className="h-4 w-4 text-green-600" />
            </Link>
          </div>
        </Card>
      )}

      {/* Quick Actions */}
      <div className="flex flex-wrap gap-3">
        <Link to="/projects" className="px-4 py-2 text-sm text-gray-700 bg-gray-50 rounded-md hover:bg-gray-100">
          View Projects
        </Link>
        <Link to="/users" className="px-4 py-2 text-sm text-gray-700 bg-gray-50 rounded-md hover:bg-gray-100">
          Manage Team
        </Link>
        <Link to="/standards" className="px-4 py-2 text-sm text-gray-700 bg-gray-50 rounded-md hover:bg-gray-100">
          Standards Config
        </Link>
        <Link to="/settings" className="px-4 py-2 text-sm text-gray-700 bg-gray-50 rounded-md hover:bg-gray-100">
          Settings
        </Link>
        <a
          href="https://mindmeld.dev/docs"
          target="_blank"
          rel="noopener noreferrer"
          className="px-4 py-2 text-sm text-gray-700 bg-gray-50 rounded-md hover:bg-gray-100"
        >
          Documentation
        </a>
      </div>
    </div>
  );
};

export default Dashboard;
